import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorService } from '../services/api';
import './Doctors.css';

const Doctors = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    firstLastName: '',
    secondLastName: '',
    email: '',
    phone: '',
    specialty: [],
    mpps: '',
    identificationCard: '',
    idType: 'V',
    idNumber: '',
    gender: 'masculino',
    consultationDuration: '',
    consultationFee: '',
    biography: '',
    whatsappNumber: '',
    acceptsEmergencies: false,
    isActive: true,
    showOnHomePage: false,
    notes: ''
  });

  const [scheduleData, setScheduleData] = useState([]);
  const [customSpecialty, setCustomSpecialty] = useState('');
  const [showCustomSpecialty, setShowCustomSpecialty] = useState(false);

  const predefinedSpecialties = [
    'Cataratas',
    'Glaucoma',
    'Retina',
    'Cirugía Refractiva',
    'Oculoplastia',
    'Córnea',
    'Pediatría Oftalmológica',
    'Neuro-oftalmología',
    'Uveítis',
    'Oftalmología General'
  ];

  const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Helper function to get doctor prefix based on gender
  const getDoctorPrefix = (doctor) => {
    if (!doctor) return 'Dr.';
    return doctor.gender === 'femenino' ? 'Dra.' : 'Dr.';
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await doctorService.getAll({ search: searchTerm });
      if (response.success) {
        setDoctors(response.data);
      }
    } catch (err) {
      setError('Error al cargar médicos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDoctors();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande. Tamaño máximo: 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Por favor seleccione un archivo de imagen válido');
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSpecialtySelect = (e) => {
    const value = e.target.value;

    if (value === 'custom') {
      setShowCustomSpecialty(true);
      return;
    }

    if (value && !formData.specialty.includes(value)) {
      setFormData(prev => ({
        ...prev,
        specialty: [...prev.specialty, value]
      }));
    }
  };

  const handleAddCustomSpecialty = () => {
    if (customSpecialty.trim() && !formData.specialty.includes(customSpecialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialty: [...prev.specialty, customSpecialty.trim()]
      }));
      setCustomSpecialty('');
      setShowCustomSpecialty(false);
    }
  };

  const handleRemoveSpecialty = (specialtyToRemove) => {
    setFormData(prev => ({
      ...prev,
      specialty: prev.specialty.filter(s => s !== specialtyToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();

      // Combine idType and idNumber into identificationCard
      const fullIdentificationCard = `${formData.idType}-${formData.idNumber}`;

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'specialty') {
          submitData.append(key, JSON.stringify(formData[key]));
        } else if (key === 'identificationCard') {
          submitData.append(key, fullIdentificationCard);
        } else if (key === 'idType' || key === 'idNumber') {
          // Skip these as they're combined into identificationCard
        } else {
          submitData.append(key, formData[key]);
        }
      });

      // Append image if selected
      if (imageFile) {
        submitData.append('profileImage', imageFile);
      }

      if (editingDoctor) {
        const response = await doctorService.update(editingDoctor._id, submitData);
        if (response.success) {
          alert('Médico actualizado exitosamente');
          setShowForm(false);
          setEditingDoctor(null);
          fetchDoctors();
          resetForm();
        }
      } else {
        const response = await doctorService.create(submitData);
        if (response.success) {
          // Show credentials if they were created
          if (response.credentials) {
            alert(
              `Médico creado exitosamente!\n\n` +
              `Credenciales de acceso generadas:\n` +
              `Email: ${response.credentials.email}\n` +
              `Contraseña temporal: ${response.credentials.tempPassword}\n\n` +
              `IMPORTANTE: Guarde esta información de forma segura.\n` +
              `El médico puede cambiar su contraseña desde su perfil.`
            );
          } else {
            alert('Médico creado exitosamente');
          }
          setShowForm(false);
          fetchDoctors();
          resetForm();
        }
      }
    } catch (err) {
      console.error('Error completo:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Error al guardar médico';
      alert(`Error: ${errorMessage}`);

      // Mostrar errores de validación si existen
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors.map(e => e.msg).join('\n');
        alert(`Errores de validación:\n${validationErrors}`);
      }
    }
  };

  const handleEdit = (doctor) => {
    setEditingDoctor(doctor);

    // Parse identificationCard (e.g., "V-12345678")
    const idParts = doctor.identificationCard.split('-');
    const idType = idParts[0] || 'V';
    const idNumber = idParts[1] || '';

    setFormData({
      firstName: doctor.firstName,
      secondName: doctor.secondName || '',
      firstLastName: doctor.firstLastName,
      secondLastName: doctor.secondLastName || '',
      email: doctor.email,
      phone: doctor.phone,
      specialty: doctor.specialty,
      mpps: doctor.mpps,
      identificationCard: doctor.identificationCard,
      idType: idType,
      idNumber: idNumber,
      gender: doctor.gender || 'masculino',
      consultationDuration: doctor.consultationDuration || '',
      consultationFee: doctor.consultationFee || '',
      biography: doctor.biography || '',
      whatsappNumber: doctor.whatsappNumber || '',
      acceptsEmergencies: doctor.acceptsEmergencies,
      isActive: doctor.isActive,
      showOnHomePage: doctor.showOnHomePage || false,
      notes: doctor.notes || ''
    });
    if (doctor.profileImage) {
      setImagePreview(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://col2c.com'}${doctor.profileImage}`);
    }
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este médico?')) {
      try {
        const response = await doctorService.delete(id);
        if (response.success) {
          alert('Médico eliminado exitosamente');
          fetchDoctors();
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar médico');
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const response = await doctorService.toggleActive(id);
      if (response.success) {
        alert(response.message);
        fetchDoctors();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error al cambiar estado');
    }
  };

  const handleManageSchedule = (doctor) => {
    setSelectedDoctor(doctor);
    setScheduleData(doctor.schedule || []);
    setShowScheduleModal(true);
  };

  const addScheduleSlot = () => {
    setScheduleData(prev => [...prev, {
      type: 'recurring', // 'recurring' or 'specific'
      day: 'Lunes',
      date: '', // For specific dates
      startTime: '08:00',
      endTime: '12:00',
      isActive: true
    }]);
  };

  const removeScheduleSlot = (index) => {
    setScheduleData(prev => prev.filter((_, i) => i !== index));
  };

  const handleScheduleChange = (index, field, value) => {
    setScheduleData(prev => {
      const newSchedule = [...prev];
      newSchedule[index] = { ...newSchedule[index], [field]: value };
      return newSchedule;
    });
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await doctorService.updateSchedule(selectedDoctor._id, scheduleData);
      if (response.success) {
        alert('Horario actualizado exitosamente');
        setShowScheduleModal(false);
        setSelectedDoctor(null);
        fetchDoctors();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error al actualizar horario');
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      secondName: '',
      firstLastName: '',
      secondLastName: '',
      email: '',
      phone: '',
      specialty: [],
      mpps: '',
      identificationCard: '',
      idType: 'V',
      idNumber: '',
      gender: 'masculino',
      consultationDuration: '',
      consultationFee: '',
      biography: '',
      whatsappNumber: '',
      acceptsEmergencies: false,
      isActive: true,
      showOnHomePage: false,
      notes: ''
    });
    setImagePreview(null);
    setImageFile(null);
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingDoctor(null);
    resetForm();
  };

  if (loading) {
    return <div className="loading">Cargando médicos...</div>;
  }

  return (
    <div className="doctors-page">
      <div className="container">
        <div className="page-header">
          <h1>Gestión de Médicos</h1>
          <div className="page-header-actions">
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              ← Volver al Dashboard
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm ? 'Cancelar' : '+ Nuevo Médico'}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Buscar por nombre, email o MPPS..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-secondary">Buscar</button>
        </form>

        {/* Form */}
        {showForm && (
          <div className="form-card">
            <h2>{editingDoctor ? 'Editar Médico' : 'Nuevo Médico'}</h2>
            <form onSubmit={handleSubmit}>
              {/* Photo Upload */}
              <div className="form-group full-width photo-upload-section">
                <label>Foto del Médico</label>
                <div className="photo-upload-container">
                  <div className="photo-preview">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" />
                    ) : (
                      <div className="photo-placeholder">
                        <span>📷</span>
                        <p>Sin foto</p>
                      </div>
                    )}
                  </div>
                  <div className="photo-upload-controls">
                    <input
                      type="file"
                      id="profileImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="profileImage" className="btn btn-secondary">
                      Seleccionar Foto
                    </label>
                    <small className="form-help">
                      Tamaño recomendado: 400x400px (Máx. 5MB)
                      <br />
                      Formatos: JPG, PNG, WebP
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Primer Nombre *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Segundo Nombre</label>
                  <input
                    type="text"
                    name="secondName"
                    value={formData.secondName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Primer Apellido *</label>
                  <input
                    type="text"
                    name="firstLastName"
                    value={formData.firstLastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Segundo Apellido</label>
                  <input
                    type="text"
                    name="secondLastName"
                    value={formData.secondLastName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Teléfono *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Cédula de Identidad *</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                      name="idType"
                      value={formData.idType}
                      onChange={handleInputChange}
                      required
                      style={{ width: '80px' }}
                    >
                      <option value="V">V</option>
                      <option value="E">E</option>
                    </select>
                    <input
                      type="text"
                      name="idNumber"
                      value={formData.idNumber}
                      onChange={handleInputChange}
                      required
                      placeholder="12345678"
                      pattern="[0-9]+"
                      title="Solo números"
                      style={{ flex: 1 }}
                    />
                  </div>
                  {editingDoctor && (
                    <small style={{ color: '#f39c12', display: 'block', marginTop: '5px' }}>
                      ⚠️ Ten cuidado al modificar la cédula, debe ser única en el sistema
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>MPPS *</label>
                  <input
                    type="text"
                    name="mpps"
                    value={formData.mpps}
                    onChange={handleInputChange}
                    required
                    placeholder="12345"
                  />
                  {editingDoctor && (
                    <small style={{ color: '#f39c12', display: 'block', marginTop: '5px' }}>
                      ⚠️ Ten cuidado al modificar el MPPS, debe ser único en el sistema
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Género *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="masculino">Masculino (Dr.)</option>
                    <option value="femenino">Femenino (Dra.)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="tel"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    placeholder="+58 414 1234567"
                  />
                </div>

                <div className="form-group">
                  <label>Duración de Consulta (minutos) <small>(opcional, por defecto: 60 min)</small></label>
                  <input
                    type="number"
                    name="consultationDuration"
                    value={formData.consultationDuration}
                    onChange={handleInputChange}
                    min="15"
                    max="120"
                    step="15"
                    placeholder="60"
                  />
                </div>

                <div className="form-group">
                  <label>Costo de Consulta ($) <small>(opcional)</small></label>
                  <input
                    type="number"
                    name="consultationFee"
                    value={formData.consultationFee}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Especialidades *</label>
                <div className="specialty-selector">
                  <select onChange={handleSpecialtySelect} value="" className="specialty-dropdown">
                    <option value="">Seleccionar especialidad...</option>
                    {predefinedSpecialties.map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                    <option value="custom">➕ Agregar otra especialidad...</option>
                  </select>

                  {showCustomSpecialty && (
                    <div className="custom-specialty-input">
                      <input
                        type="text"
                        value={customSpecialty}
                        onChange={(e) => setCustomSpecialty(e.target.value)}
                        placeholder="Escriba la especialidad"
                      />
                      <button type="button" className="btn btn-secondary" onClick={handleAddCustomSpecialty}>
                        Agregar
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => {
                        setShowCustomSpecialty(false);
                        setCustomSpecialty('');
                      }}>
                        Cancelar
                      </button>
                    </div>
                  )}

                  <div className="selected-specialties">
                    {formData.specialty.map((spec, idx) => (
                      <span key={idx} className="specialty-tag">
                        {spec}
                        <button
                          type="button"
                          className="remove-specialty"
                          onClick={() => handleRemoveSpecialty(spec)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Biografía</label>
                <textarea
                  name="biography"
                  value={formData.biography}
                  onChange={handleInputChange}
                  rows="4"
                  maxLength="1000"
                  placeholder="Breve descripción profesional..."
                />
              </div>

              <div className="form-group full-width">
                <label>Notas Internas</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Notas privadas sobre el médico..."
                />
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="acceptsEmergencies"
                    checked={formData.acceptsEmergencies}
                    onChange={handleInputChange}
                  />
                  Acepta Emergencias
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Activo
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="showOnHomePage"
                    checked={formData.showOnHomePage}
                    onChange={handleInputChange}
                  />
                  Mostrar en Página Principal
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingDoctor ? 'Actualizar' : 'Crear'} Médico
                </button>
                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Doctors List */}
        <div className="doctors-grid">
          {doctors.map(doctor => (
            <div key={doctor._id} className={`doctor-card ${!doctor.isActive ? 'inactive' : ''}`}>
              {doctor.profileImage && (
                <div className="doctor-photo">
                  <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://col2c.com'}${doctor.profileImage}`} alt={doctor.firstName} />
                </div>
              )}

              <div className="doctor-header">
                <h3>{doctor.firstName} {doctor.secondName} {doctor.firstLastName} {doctor.secondLastName}</h3>
                <span className={`status-badge ${doctor.isActive ? 'active' : 'inactive'}`}>
                  {doctor.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <div className="doctor-info">
                <p><strong>Email:</strong> {doctor.email}</p>
                <p><strong>Teléfono:</strong> {doctor.phone}</p>
                <p><strong>C.I.:</strong> {doctor.identificationCard}</p>
                <p><strong>MPPS:</strong> {doctor.mpps}</p>

                <div className="specialties">
                  <strong>Especialidades:</strong>
                  <div className="specialty-tags">
                    {doctor.specialty.map((spec, idx) => (
                      <span key={idx} className="specialty-tag">{spec}</span>
                    ))}
                  </div>
                </div>

                {doctor.consultationDuration && (
                  <p><strong>Duración Consulta:</strong> {doctor.consultationDuration} min</p>
                )}

                {doctor.consultationFee > 0 && (
                  <p><strong>Costo Consulta:</strong> ${doctor.consultationFee}</p>
                )}

                {doctor.whatsappNumber && (
                  <p><strong>WhatsApp:</strong> {doctor.whatsappNumber}</p>
                )}

                {doctor.acceptsEmergencies && (
                  <span className="emergency-badge">Acepta Emergencias</span>
                )}

                {doctor.schedule && doctor.schedule.length > 0 && (
                  <div className="schedule-summary">
                    <strong>Horarios:</strong> {doctor.schedule.length} días configurados
                  </div>
                )}
              </div>

              <div className="doctor-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleEdit(doctor)}
                >
                  Editar
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleManageSchedule(doctor)}
                >
                  Horarios
                </button>
                <button
                  className={`btn btn-sm ${doctor.isActive ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleToggleActive(doctor._id)}
                >
                  {doctor.isActive ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(doctor._id)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>

        {doctors.length === 0 && !loading && (
          <div className="no-data">
            No hay médicos registrados
          </div>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && selectedDoctor && (
          <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Gestionar Horarios</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowScheduleModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <h3>{getDoctorPrefix(selectedDoctor)} {selectedDoctor.firstName} {selectedDoctor.firstLastName}</h3>

                <button
                  className="btn btn-secondary mb-3"
                  onClick={addScheduleSlot}
                  type="button"
                >
                  + Agregar Horario
                </button>

                <div className="schedule-list">
                  {scheduleData.map((slot, index) => (
                    <div key={index} className="schedule-slot">
                      <div className="schedule-slot-fields">
                        <div className="schedule-type-selector">
                          <label>
                            <input
                              type="radio"
                              name={`type-${index}`}
                              value="recurring"
                              checked={slot.type === 'recurring' || !slot.type}
                              onChange={(e) => handleScheduleChange(index, 'type', e.target.value)}
                            />
                            Horario Recurrente
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`type-${index}`}
                              value="specific"
                              checked={slot.type === 'specific'}
                              onChange={(e) => handleScheduleChange(index, 'type', e.target.value)}
                            />
                            Fecha Específica
                          </label>
                        </div>

                        {(slot.type === 'recurring' || !slot.type) ? (
                          <select
                            value={slot.day}
                            onChange={(e) => handleScheduleChange(index, 'day', e.target.value)}
                            className="schedule-input"
                          >
                            {daysOfWeek.map(day => (
                              <option key={day} value={day}>{day}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="date"
                            value={slot.date}
                            onChange={(e) => handleScheduleChange(index, 'date', e.target.value)}
                            className="schedule-input"
                            required
                          />
                        )}

                        <div className="time-inputs">
                          <label>
                            Desde:
                            <input
                              type="time"
                              value={slot.startTime}
                              onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                              className="schedule-input"
                            />
                          </label>

                          <label>
                            Hasta:
                            <input
                              type="time"
                              value={slot.endTime}
                              onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                              className="schedule-input"
                            />
                          </label>
                        </div>

                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={slot.isActive}
                            onChange={(e) => handleScheduleChange(index, 'isActive', e.target.checked)}
                          />
                          Activo
                        </label>

                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeScheduleSlot(index)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {scheduleData.length === 0 && (
                  <p className="no-data">No hay horarios configurados</p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSchedule}
                >
                  Guardar Horarios
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowScheduleModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Doctors;
